import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { TeamService } from 'app/shared/services/team.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-edit-user-dialog',
    templateUrl: './edit-user-dialog.component.html',
    styleUrl: './edit-user-dialog.component.scss',
    imports: [
        CommonModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatInputModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatDialogModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditUserDialogComponent implements OnInit {
    readonly dialogRef = inject(MatDialogRef<EditUserDialogComponent>);
    readonly data = inject<any>(MAT_DIALOG_DATA);

    userForm: UntypedFormGroup;
    isLoading: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _formBuilder: UntypedFormBuilder,
        private teamService: TeamService,
        private snackBar: MatSnackBar
    ) {
        this.userForm = this._formBuilder.group({
            name: ['', Validators.required],
            email: [{ value: '', disabled: true }],
            phone: [''],
            country: [''],
            language: [''],
        });
    }

    ngOnInit(): void {
        if (this.data?.user) {
            this.loadUserData(this.data.user);
        }
    }

    private loadUserData(user: any): void {
        this.userForm.patchValue({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            country: user.country || '',
            language: user.language || 'en',
        });
    }

    onSubmit(): void {
        if (this.userForm.invalid || this.isLoading) {
            return;
        }

        this.isLoading = true;

        const formValue = this.userForm.value;
        const userId = this.data.user._id;

        // Update the user as super admin
        this.teamService
            .updateUserAsSuperAdmin(userId, formValue)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.snackBar.open('User updated successfully', 'Close', {
                        duration: 3000,
                    });
                    this.dialogRef.close(response);
                },
                error: (err) => {
                    console.error('Error updating user:', err);
                    // Show the actual error message from the API
                    this.snackBar.open(err.message || 'Error updating user', 'Close', {
                        duration: 5000,
                    });
                    this.isLoading = false;
                },
            });
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
}
